import React, { useCallback, useEffect, useState } from 'react';
import { ORGANIZER_PORTAL_URL } from '../constants/venueLinks';

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box' };
const btnStyle = { padding: '8px 14px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const btnPrimary = { ...btnStyle, background: '#007bff', color: '#fff', borderColor: '#007bff' };
const btnPortal = {
  ...btnStyle,
  background: '#0369a1',
  color: '#fff',
  borderColor: '#075985',
  fontWeight: 700,
  textDecoration: 'none',
  display: 'inline-block',
};
const msgError = { padding: '10px 12px', marginBottom: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: 13, border: '1px solid #fecaca' };
const msgOk = { padding: '10px 12px', marginBottom: 12, background: '#ecfdf5', color: '#166534', borderRadius: 8, fontSize: 13, border: '1px solid #bbf7d0' };
const inactiveTag = {
  display: 'inline-block',
  marginLeft: 6,
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  background: '#f3f4f6',
  color: '#6b7280',
};

function permMark(on) {
  return on ? '✓' : '—';
}

function formatBoundMemberLabel(row, mid) {
  if (mid == null || mid === '') return '';
  const name = String(row?.MEMBER_NAME ?? row?.memberName ?? '').trim();
  const phone = String(row?.MEMBER_PHONE ?? row?.memberPhone ?? '').trim();
  const parts = [name, phone].filter(Boolean);
  if (parts.length) return `${parts.join('／')} (#${mid})`;
  return `會員 #${mid}`;
}

function featureSummary(row) {
  const active = !!(row?.IS_ACTIVE ?? row?.isActive);
  const product = !!(row?.CAN_CREATE_PRODUCT ?? row?.canCreateProduct);
  return { calendar: active, product };
}

export default function OrganizerAccountsSection({ adminAuthHeaders, formSectionStyle }) {
  const [rows, setRows] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [globalNpid, setGlobalNpid] = useState('');
  const [globalSaving, setGlobalSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState('');
  const [pageSuccess, setPageSuccess] = useState('');
  const [formError, setFormError] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({
    login: '', password: '', displayName: '', note: '', email: '', isActive: true,
    canCreateProduct: false, defaultNotificationProfileId: '', memberId: '', lineGroupId: '',
  });
  const [memberQ, setMemberQ] = useState('');
  const [memberHits, setMemberHits] = useState([]);
  const [memberCache, setMemberCache] = useState(null);
  const [memberSearching, setMemberSearching] = useState(false);
  const [boundMemberLabel, setBoundMemberLabel] = useState('');
  const [lineGroups, setLineGroups] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const safeJson = async (res) => {
        const text = await res.text();
        try { return JSON.parse(text); } catch { return { ok: false, error: res.ok ? '回應格式錯誤' : `HTTP ${res.status}` }; }
      };
      const [orgRes, npRes, globalRes, lgRes] = await Promise.all([
        fetch('/api/vb/admin/organizers', { headers: adminAuthHeaders() }),
        fetch('/api/vb/notification-profiles', { headers: adminAuthHeaders() }),
        fetch('/api/vb/admin/organizer-notification-default', { headers: adminAuthHeaders() }),
        fetch('/api/vb/admin/line-groups', { headers: adminAuthHeaders() }),
      ]);
      const [orgData, npData, globalData, lgData] = await Promise.all([
        safeJson(orgRes), safeJson(npRes), safeJson(globalRes), safeJson(lgRes),
      ]);
      if (orgData?.ok) setRows(orgData.data || []);
      else setPageError(orgData?.error || '載入主辦帳號失敗');
      if (globalData?.ok) {
        setGlobalNpid(String(globalData.data?.defaultNotificationProfileId ?? '') || '');
      }
      if (lgData?.ok) setLineGroups(lgData.data || []);
      else setLineGroups([]);
      if (npData?.ok) {
        const list = (npData.data || []).filter((p) => {
          const pol = String(p.REVIEW_POLICY ?? p.reviewPolicy ?? '').toLowerCase();
          const active = p.IS_ACTIVE ?? p.isActive;
          return pol === 'no_review' && (active === true || active === 1 || active == null);
        });
        setProfiles(list);
      }
    } catch (e) {
      console.error(e);
      setPageError('載入失敗');
    } finally {
      setLoading(false);
    }
  }, [adminAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  // 會員綁定搜尋：目前一次載入全會員清單再前端篩；會員量大時建議改 server 端搜尋 API
  const ensureMemberCache = async () => {
    if (Array.isArray(memberCache)) return memberCache;
    setMemberSearching(true);
    try {
      const res = await fetch('/api/admin/members', { headers: adminAuthHeaders(), credentials: 'include' });
      const list = await res.json();
      const arr = Array.isArray(list) ? list : [];
      setMemberCache(arr);
      return arr;
    } catch {
      setMemberCache([]);
      return [];
    } finally {
      setMemberSearching(false);
    }
  };

  const searchMembers = async (q) => {
    const query = String(q || '').trim();
    setMemberQ(q);
    if (!query) {
      setMemberHits([]);
      return;
    }
    const arr = await ensureMemberCache();
    const ql = query.toLowerCase();
    setMemberHits(
      arr
        .filter((m) => (
          String(m.NAME || '').toLowerCase().includes(ql)
          || String(m.PHONE || '').includes(query)
          || String(m.ID || '') === query
        ))
        .slice(0, 20),
    );
  };

  const openCreate = () => {
    setFormError('');
    setForm({
      login: '', password: '', displayName: '', note: '', email: '', isActive: true,
      canCreateProduct: false, defaultNotificationProfileId: '', memberId: '', lineGroupId: '',
    });
    setMemberQ('');
    setMemberHits([]);
    setBoundMemberLabel('');
    setModal({ mode: 'create' });
  };

  const openEdit = (row) => {
    const id = row.ID ?? row.id;
    const mid = row.MEMBER_ID ?? row.memberId ?? null;
    setFormError('');
    setForm({
      login: row.LOGIN ?? row.login ?? '',
      password: '',
      displayName: row.DISPLAY_NAME ?? row.displayName ?? '',
      note: row.NOTE ?? row.note ?? '',
      email: row.EMAIL ?? row.email ?? '',
      isActive: !!(row.IS_ACTIVE ?? row.isActive),
      canCreateProduct: !!(row.CAN_CREATE_PRODUCT ?? row.canCreateProduct),
      defaultNotificationProfileId: String(row.DEFAULT_NOTIFICATION_PROFILE_ID ?? row.defaultNotificationProfileId ?? '') || '',
      memberId: mid != null ? String(mid) : '',
      lineGroupId: String(row.LINE_GROUP_ID ?? row.lineGroupId ?? '') || '',
    });
    setBoundMemberLabel(formatBoundMemberLabel(row, mid));
    setMemberQ('');
    setMemberHits([]);
    setModal({ mode: 'edit', id });
  };

  const submit = async () => {
    setFormError('');
    if (modal?.mode === 'create') {
      if (!form.login.trim() || !form.password) {
        setFormError('請填帳號與密碼');
        return;
      }
      if (form.password.length < 6) {
        setFormError('密碼至少 6 字元');
        return;
      }
    }
    if (form.canCreateProduct && !form.defaultNotificationProfileId && !globalNpid) {
      setFormError('允許上架商品時，請設定「全系統預設通知設定檔」或為此主辦指定個別設定檔');
      return;
    }
    try {
      const body = {
        displayName: form.displayName || null,
        note: form.note || null,
        email: form.email || null,
        isActive: form.isActive,
        canCreateProduct: form.canCreateProduct,
        defaultNotificationProfileId: form.defaultNotificationProfileId ? Number(form.defaultNotificationProfileId) : null,
        memberId: form.memberId !== '' ? Number(form.memberId) : null,
        lineGroupId: form.lineGroupId || null,
      };
      let res;
      if (modal.mode === 'create') {
        res = await fetch('/api/vb/admin/organizers', {
          method: 'POST',
          headers: adminAuthHeaders(),
          body: JSON.stringify({ ...body, login: form.login.trim(), password: form.password }),
        });
      } else {
        if (form.password) body.password = form.password;
        if (form.login?.trim()) body.login = form.login.trim();
        res = await fetch(`/api/vb/admin/organizers/${modal.id}`, {
          method: 'PUT',
          headers: adminAuthHeaders(),
          body: JSON.stringify(body),
        });
      }
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '儲存失敗');
      setModal(null);
      setPageSuccess('已儲存');
      load();
    } catch (e) {
      setFormError(e.message || '儲存失敗');
    }
  };

  const saveGlobalDefault = async () => {
    setPageError('');
    setPageSuccess('');
    if (!globalNpid) {
      setPageError('請選擇全系統預設通知設定檔');
      return;
    }
    setGlobalSaving(true);
    try {
      const res = await fetch('/api/vb/admin/organizer-notification-default', {
        method: 'PUT',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ defaultNotificationProfileId: Number(globalNpid) }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '儲存失敗');
      setPageSuccess('已儲存全系統預設');
      load();
    } catch (e) {
      setPageError(e.message || '儲存失敗');
    } finally {
      setGlobalSaving(false);
    }
  };

  return (
    <section style={{ ...formSectionStyle, marginBottom: 24 }}>
      {pageError ? <div style={msgError}>{pageError}</div> : null}
      {pageSuccess ? <div style={msgOk}>{pageSuccess}</div> : null}

      <div style={{ marginBottom: 16, padding: 12, background: '#f0f7ff', borderRadius: 8, border: '1px solid #cce5ff' }}>
        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>全系統預設通知設定檔</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={{ ...inputStyle, maxWidth: 320, marginBottom: 0 }} value={globalNpid} onChange={(e) => setGlobalNpid(e.target.value)}>
            <option value="">— 請選擇 —</option>
            {profiles.map((p) => (
              <option key={p.ID ?? p.id} value={p.ID ?? p.id}>{p.NAME ?? p.name}</option>
            ))}
          </select>
          <button type="button" style={btnPrimary} disabled={globalSaving} onClick={saveGlobalDefault}>儲存全系統預設</button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>主辦帳號</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <a href="/admin/venue/notification-profiles" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#007bff' }}>
            管理通知模板
          </a>
          <a href={ORGANIZER_PORTAL_URL} target="_blank" rel="noopener noreferrer" style={btnPortal}>
            前往主辦前台 ↗
          </a>
          <button type="button" style={btnPrimary} onClick={openCreate}>新增主辦</button>
        </div>
      </div>
      {loading ? (
        <p style={{ color: '#666' }}>載入中…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: '#9ca3af', padding: '16px 0' }}>尚無主辦帳號</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              {['ID', '帳號', '顯示名稱', '會員', 'Email', '月曆', '商品', '通知設定檔', '啟用', ''].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const id = r.ID ?? r.id;
              const feats = featureSummary(r);
              const mid = r.MEMBER_ID ?? r.memberId;
              const isActive = !!(r.IS_ACTIVE ?? r.isActive);
              const rowStyle = {
                padding: 8,
                borderBottom: '1px solid #eee',
                background: isActive ? undefined : '#f9fafb',
                color: isActive ? undefined : '#6b7280',
              };
              return (
                <tr key={id}>
                  <td style={rowStyle}>{id}</td>
                  <td style={rowStyle}>
                    {r.LOGIN ?? r.login}
                    {!isActive ? <span style={inactiveTag}>已停用</span> : null}
                  </td>
                  <td style={rowStyle}>{r.DISPLAY_NAME ?? r.displayName}</td>
                  <td style={rowStyle}>{mid != null ? formatBoundMemberLabel(r, mid) : '—'}</td>
                  <td style={rowStyle}>{r.EMAIL ?? r.email ?? '—'}</td>
                  <td style={rowStyle} title={feats.calendar ? '帳號啟用，可使用月曆' : '帳號停用'}>{permMark(feats.calendar)}</td>
                  <td style={rowStyle} title={feats.product ? '已開放線上報名商品' : '未開放'}>{permMark(feats.product)}</td>
                  <td style={rowStyle}>
                    {r.NOTIFICATION_PROFILE_NAME ?? (r.CAN_CREATE_PRODUCT || r.canCreateProduct ? '（套用全系統預設）' : '—')}
                  </td>
                  <td style={rowStyle}>{isActive ? '是' : '否'}</td>
                  <td style={rowStyle}>
                    <button type="button" style={btnStyle} onClick={() => openEdit(r)}>編輯</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        >
          <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: 420, maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>{modal.mode === 'create' ? '新增主辦' : '編輯主辦'}</h3>
            {formError ? <div style={{ ...msgError, marginTop: 0 }}>{formError}</div> : null}
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#f8f9fa', borderRadius: 8, fontSize: 13, color: '#444' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>功能權限</div>
              <div>月曆活動：{form.isActive ? '已開啟（帳號啟用）' : '未開啟（帳號停用）'}</div>
              <div>線上報名商品：{form.canCreateProduct ? '已開啟' : '未開啟'}</div>
            </div>
            {modal.mode === 'create' && (
              <>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>登入帳號 *</label>
                <input style={{ ...inputStyle, marginBottom: 10 }} value={form.login} onChange={(e) => setForm((f) => ({ ...f, login: e.target.value }))} />
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>密碼 *</label>
                <input type="password" style={{ ...inputStyle, marginBottom: 10 }} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              </>
            )}
            {modal.mode === 'edit' && (
              <>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>登入帳號</label>
                <input style={{ ...inputStyle, marginBottom: 10 }} value={form.login} onChange={(e) => setForm((f) => ({ ...f, login: e.target.value }))} />
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>新密碼（留空不變）</label>
                <input type="password" style={{ ...inputStyle, marginBottom: 10 }} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              </>
            )}
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>顯示名稱</label>
            <input style={{ ...inputStyle, marginBottom: 10 }} value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} />
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>通知 Email</label>
            <input style={{ ...inputStyle, marginBottom: 10 }} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>LINE 通知群組</label>
            <select
              style={{ ...inputStyle, marginBottom: 10 }}
              value={form.lineGroupId || ''}
              onChange={(e) => setForm((f) => ({ ...f, lineGroupId: e.target.value }))}
            >
              <option value="">不綁定</option>
              {lineGroups.map((g) => {
                const gid = g.groupId || g.GROUP_ID;
                const name = g.groupName || g.GROUP_NAME || '';
                const st = g.status || g.STATUS;
                const label = name ? `${name} (${String(gid).slice(0, 8)}…)` : gid;
                return (
                  <option key={gid} value={gid}>
                    {st === 'left' ? `[已離開] ${label}` : label}
                  </option>
                );
              })}
            </select>
            {lineGroups.length === 0 && (
              <div style={{ fontSize: 12, color: '#888', marginTop: -6, marginBottom: 10 }}>
                尚無名冊：請在群組內對官方帳號發一則訊息後重新載入
              </div>
            )}
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>備註</label>
            <input style={{ ...inputStyle, marginBottom: 10 }} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />

            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>綁定會員</label>
            {form.memberId ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
                <span>{boundMemberLabel || `會員 #${form.memberId}`}</span>
                <button
                  type="button"
                  style={{ ...btnStyle, padding: '4px 10px', fontSize: 12 }}
                  onClick={() => {
                    setForm((f) => ({ ...f, memberId: '' }));
                    setBoundMemberLabel('');
                    setMemberHits([]);
                  }}
                >
                  清除
                </button>
              </div>
            ) : null}
            <input
              style={{ ...inputStyle, marginBottom: 6 }}
              value={memberQ}
              onChange={(e) => searchMembers(e.target.value)}
              placeholder="搜尋姓名／電話"
            />
            {memberSearching && <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>搜尋中…</div>}
            {memberHits.length > 0 && (
              <div style={{
                border: '1px solid #ddd', borderRadius: 6, maxHeight: 160, overflow: 'auto',
                marginBottom: 10, background: '#fff',
              }}
              >
                {memberHits.map((m) => {
                  const mid = m.ID ?? m.id;
                  return (
                    <button
                      key={mid}
                      type="button"
                      onClick={() => {
                        setForm((f) => ({ ...f, memberId: String(mid) }));
                        setBoundMemberLabel(`${m.NAME || '（未命名）'} / ${m.PHONE || '—'} (#${mid})`);
                        setMemberHits([]);
                        setMemberQ('');
                      }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                        border: 'none', borderBottom: '1px solid #eee', background: '#fff',
                        cursor: 'pointer', fontSize: 13,
                      }}
                    >
                      {m.NAME || '（未命名）'} · {m.PHONE || '—'} · #{mid}
                    </button>
                  );
                })}
              </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 14 }}>
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
              啟用
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 14 }}>
              <input type="checkbox" checked={form.canCreateProduct} onChange={(e) => setForm((f) => ({ ...f, canCreateProduct: e.target.checked }))} />
              允許上架商品（線上報名／財務）
            </label>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
              個別通知設定檔（留空則用全系統預設）{form.canCreateProduct && !globalNpid ? ' *' : ''}
            </label>
            <select
              style={{ ...inputStyle, marginBottom: 14 }}
              value={form.defaultNotificationProfileId}
              onChange={(e) => setForm((f) => ({ ...f, defaultNotificationProfileId: e.target.value }))}
            >
              <option value="">— 使用全系統預設 —</option>
              {profiles.map((p) => (
                <option key={p.ID ?? p.id} value={p.ID ?? p.id}>{p.NAME ?? p.name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" style={btnStyle} onClick={() => setModal(null)}>取消</button>
              <button type="button" style={btnPrimary} onClick={submit}>儲存</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
